import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState, type JSX } from 'react';

import { submitGeneralFeedback } from '@/lib/api/general-feedback.js';
import { notify } from '@/lib/notify.js';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';

export interface GeneralFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GeneralFeedbackDialog({
  open,
  onOpenChange,
}: GeneralFeedbackDialogProps): JSX.Element | null {
  if (!open) return null;
  return <GeneralFeedbackDialogSession onOpenChange={onOpenChange} />;
}

function GeneralFeedbackDialogSession({
  onOpenChange,
}: Pick<GeneralFeedbackDialogProps, 'onOpenChange'>): JSX.Element {
  const [comment, setComment] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const feedback = useMutation({ mutationFn: submitGeneralFeedback });
  const textarea = useRef<HTMLTextAreaElement>(null);
  const trimmedComment = comment.trim();
  const active = useRef(true);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  function close(): void {
    active.current = false;
    onOpenChange(false);
  }

  async function submit(): Promise<void> {
    if (feedback.isPending || trimmedComment === '') return;
    setFailure(null);
    try {
      await feedback.mutateAsync(trimmedComment);
      if (!active.current) return;
      setComment('');
      onOpenChange(false);
      notify.success('Thanks for the feedback');
    } catch (error) {
      if (!active.current) return;
      setFailure(
        error instanceof Error ? error.message : 'Could not send feedback.',
      );
    }
  }

  return (
    <AppDialog
      open
      onOpenChange={(next) => {
        if (!next) close();
      }}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        textarea.current?.focus();
      }}
      title="Share feedback"
      description="Send a note directly to the team."
      className="sm:max-w-lg"
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1.5 text-mini font-semibold tracking-wider text-muted-foreground">
          Your feedback
          <textarea
            ref={textarea}
            value={comment}
            rows={5}
            maxLength={5000}
            required
            disabled={feedback.isPending}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Tell us what you liked or share any other thoughts"
            className="w-full resize-none rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm font-normal normal-case tracking-normal text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-foreground/30 focus:bg-card focus:ring-2 focus:ring-foreground/10"
          />
        </label>
        {failure !== null && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={feedback.isPending || trimmedComment === ''}
          >
            Send feedback
          </Button>
        </div>
      </form>
    </AppDialog>
  );
}
